import type { ScoreGame, ScoreRound, Suit, TrumpSuit } from '@shared/types/game';
import { SUITS } from '@shared/types/game';
import {
  SUIT_SYMBOLS,
  biddingOrderFor,
  bidPressure,
  forbiddenBidFor,
  isBidLegal,
  isRedSuit,
} from '@shared/game-logic';
import { Button, Hint, NumberPad, Panel } from '@/components/ui';
import { SUIT_LABELS, plural, seatColor } from '@/lib/labels';
import { useScorekeeper } from '@/store/scorekeeper';

/**
 * Bidding, in table order: everyone bids starting to the dealer's left, and the
 * dealer bids last carrying the restriction.
 */
export default function BidStep({ game, round }: { game: ScoreGame; round: ScoreRound }) {
  const setBid = useScorekeeper((state) => state.setBid);
  const setTrump = useScorekeeper((state) => state.setTrump);
  const confirmBids = useScorekeeper((state) => state.confirmBids);

  const { houseRules } = game.config;
  const order = biddingOrderFor(round.dealerSeat, game.seats.length);
  const dealerSeat = round.dealerSeat;

  const bidOf = (seat: number) => round.entries.find((entry) => entry.seat === seat)?.bid ?? null;

  // The dealer's restriction only exists once everyone before them has bid.
  const othersBids = order.filter((seat) => seat !== dealerSeat).map(bidOf);
  const allOthersBid = othersBids.every((bid) => bid !== null);
  const forbiddenForDealer = allOthersBid
    ? forbiddenBidFor(round.cards, othersBids as number[], houseRules)
    : null;

  const everyoneBid = order.every((seat) => bidOf(seat) !== null);
  const dealerBid = bidOf(dealerSeat);
  const dealerBidLegal =
    dealerBid === null ||
    !allOthersBid ||
    isBidLegal(dealerBid, round.cards, othersBids as number[], true, houseRules);

  const pressure = bidPressure(round.cards, round.entries);

  return (
    <div className="space-y-4">
      <Panel title="Troef">
        <div className="flex flex-wrap gap-2">
          {SUITS.map((suit: Suit) => (
            <TrumpButton
              key={suit}
              active={round.trump === suit}
              onClick={() => setTrump(game.id, round.roundNo, suit)}
            >
              <span className={isRedSuit(suit) ? 'text-red-400' : 'text-slate-200'}>
                {SUIT_SYMBOLS[suit]}
              </span>{' '}
              {SUIT_LABELS[suit]}
            </TrumpButton>
          ))}
          <TrumpButton
            active={round.trump === null}
            onClick={() => setTrump(game.id, round.roundNo, null as TrumpSuit)}
          >
            troefloos
          </TrumpButton>
        </div>
        <p className="mt-2 text-sm text-slate-500">
          Optioneel — handig om later terug te zien, maar niet nodig om te tellen.
        </p>
      </Panel>

      {order.map((seat, index) => {
        const isDealer = seat === dealerSeat;
        const player = game.seats[seat];
        const disabled = isDealer && forbiddenForDealer !== null ? [forbiddenForDealer] : [];

        return (
          <Panel
            key={seat}
            title={
              <span className="flex items-center gap-2 normal-case">
                <span
                  className="h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: seatColor(seat) }}
                />
                <span className="text-base font-semibold tracking-normal text-white">
                  {player.displayName}
                </span>
                {isDealer && (
                  <span className="rounded-full bg-base-600 px-2 py-0.5 text-xs tracking-normal text-slate-300">
                    deler, biedt laatst
                  </span>
                )}
              </span>
            }
            action={<span className="text-xs text-slate-500">{index + 1}e</span>}
          >
            <NumberPad
              max={round.cards}
              value={bidOf(seat)}
              disabledValues={disabled}
              groupLabel={`${player.displayName} bod`}
              onSelect={(bid) => setBid(game.id, round.roundNo, seat, bid)}
            />
            {isDealer && forbiddenForDealer !== null && (
              <Hint title={`${forbiddenForDealer} mag niet`}>
                De anderen hebben samen {round.cards - forbiddenForDealer} geboden. Zou{' '}
                {player.displayName} nu {forbiddenForDealer} bieden, dan is het totaal precies{' '}
                {round.cards} — en dan zou iedereen zijn bod kunnen halen. Daarom mag de deler juist
                dat getal niet zeggen: er moet altijd minstens één speler fout zitten.
              </Hint>
            )}
          </Panel>
        );
      })}

      <Panel title="Totaal geboden">
        <p className="text-2xl font-semibold tabular-nums">
          {round.entries.reduce((sum, entry) => sum + (entry.bid ?? 0), 0)}
          <span className="text-base font-normal text-slate-400"> van {round.cards}</span>
        </p>
        <p className="mt-1 text-sm text-slate-400">
          {!everyoneBid
            ? 'Tot nu toe — de rest moet nog bieden.'
            : pressure > 0
              ? `Er is ${pressure} te veel geboden — minstens ${pressure} ${plural(pressure, 'speler haalt', 'spelers halen')} het niet.`
              : pressure < 0
                ? `Er is ${-pressure} te weinig geboden — er ${plural(-pressure, 'gaat', 'gaan')} ${-pressure} ${plural(-pressure, 'slag', 'slagen')} naar iemand die er niet om vroeg.`
                : 'Precies passend, dus iedereen kan zijn bod halen. Met de delersbeperking aan kan dit niet gebeuren.'}
        </p>
      </Panel>

      <Button
        variant="primary"
        full
        disabled={!everyoneBid || !dealerBidLegal}
        onClick={() => confirmBids(game.id, round.roundNo)}
      >
        {everyoneBid ? 'Biedingen klaar, ga spelen' : 'Nog niet iedereen heeft geboden'}
      </Button>
    </div>
  );
}

function TrumpButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={[
        'min-h-11 rounded-xl px-3 text-sm',
        active ? 'bg-accent-500 font-semibold text-base-950' : 'bg-base-700 hover:bg-base-600',
      ].join(' ')}
    >
      {children}
    </button>
  );
}
